import { ref, onMounted } from 'vue';
import { useAppStore } from '@store/app';
import usesGooglePlacesE2eMock from '@utilities/usesGooglePlacesE2eMock';

export default function useGoogleAutocompleteService() {
    const app = useAppStore();
    const autoCompleteService = ref(null);
    const error = ref(null);
    const hasError = ref(false);
    const loading = ref(false);
    const fetching = ref(false);
    const autoCompleteSessionToken = ref(null);

    async function initGoogle() {
        if (usesGooglePlacesE2eMock()) {
            return;
        }

        try {
            loading.value = true;

            // grab pre-initiated app level autocomplete service
            autoCompleteService.value = app.autoCompleteService;

            startNewSession();

            error.value = null;
            hasError.value = false;
        } catch (e) {
            console.error('Failed to initialize Google Places API', e);
            error.value = e;
        } finally {
            loading.value = false;
        }
    }

    async function fetchResults(searchText, callback, options = {}) {
        if (usesGooglePlacesE2eMock()) {
            fetching.value = true;
            callback([
                {
                    place_id: 'e2e_mock_place',
                    description: `${searchText}, QLD, Australia`,
                    types: ['street_address'],
                    structured_formatting: {
                        main_text: searchText,
                        main_text_matched_substrings: [{ offset: 0, length: searchText.length }],
                        secondary_text: 'QLD, Australia',
                    },
                },
            ]);
            fetching.value = false;
            return;
        }

        fetching.value = true;

        if (!autoCompleteService.value) {
            await initGoogle();
        }

        options = {
            input: searchText,
            componentRestrictions: { country: ['au'] },
            sessionToken: autoCompleteSessionToken.value,
            ...options,
        };

        autoCompleteService.value.getPlacePredictions(options, (predictions) => {
            callback(predictions);
            fetching.value = false;
        });
    }

    // Group autocomplete requests under one token so Google bills them as one session. A
    // token is valid for one session only, so a new one starts after each selection. It is
    // per component, not app level, because each address field runs its own sessions.
    function startNewSession() {
        if (app.google?.maps?.places) {
            autoCompleteSessionToken.value = new app.google.maps.places.AutocompleteSessionToken();
        }
    }

    onMounted(() => {
        initGoogle();
    });

    return {
        autoCompleteService,
        fetchResults,
        startNewSession,
        initGoogle,
        error,
        hasError,
        loading,
        fetching,
    };
}
